import { apiFetch as fetch } from "../lib/security";
import { useEffect, useState } from "react";
import { Check, Mail, X } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/motion/Reveal";
import { siteInfo } from "../data/site";

type UnsubscribeStatus = "idle" | "loading" | "success" | "error";

export function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";
  const token = searchParams.get("token") ?? "";
  const [status, setStatus] = useState<UnsubscribeStatus>(email && token ? "loading" : "idle");
  const [message, setMessage] = useState(email && token ? "Updating your email preferences..." : "Open the unsubscribe link from your email.");

  useEffect(() => {
    if (!email || !token) {
      return;
    }

    let active = true;

    async function unsubscribe() {
      setStatus("loading");
      setMessage("Updating your email preferences...");

      try {
        const response = await fetch("/api/marketing/unsubscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, token }),
        });
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(data?.error?.message ?? "Unable to unsubscribe this email.");
        }

        if (active) {
          setStatus("success");
          setMessage("You have been unsubscribed from Deposit Seed House offers and discount emails.");
        }
      } catch (error) {
        if (active) {
          setStatus("error");
          setMessage(error instanceof Error ? error.message : "Unable to unsubscribe this email.");
        }
      }
    }

    void unsubscribe();

    return () => {
      active = false;
    };
  }, [email, token]);

  return (
    <>
      <SEO
        title="Email Preferences | The Deposit Seed House"
        description="Update Deposit Seed House email preferences."
        image={siteInfo.exteriorImage}
        canonicalPath="/unsubscribe"
      />
      <section className="page-hero page-hero--account page-hero--unsubscribe">
        <Reveal>
          <p className="eyebrow">Email preferences</p>
          <h1>Manage Deposit Seed House emails.</h1>
          <p>{email || "Guest offers and direct-booking updates."}</p>
          <div className="page-hero__actions">
            <Link className="button button--primary" to="/account">
              Account settings
            </Link>
            <Link className="button button--light" to="/apartments">
              View apartments
            </Link>
          </div>
        </Reveal>
      </section>
      <section className="section account-section">
        <Reveal className="unsubscribe-card">
          <div className={`unsubscribe-card__icon unsubscribe-card__icon--${status}`}>
            {status === "success" ? <Check size={28} aria-hidden="true" /> : status === "error" ? <X size={28} aria-hidden="true" /> : <Mail size={28} aria-hidden="true" />}
          </div>
          <div>
            <p className="eyebrow">Email list</p>
            <h2>{status === "success" ? "Unsubscribed" : status === "error" ? "Unable to unsubscribe" : "Updating preferences"}</h2>
            <p>{message}</p>
          </div>
          <div className="unsubscribe-card__actions">
            <Link className="button button--secondary" to="/account">
              Account settings
            </Link>
            <Link className="text-link" to="/apartments">
              View apartments
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
